import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { NAVBAR_MENU_KEYS, NS } from '../../../i18n/constants/keys';
import {
  CompassIcon,
  HeartHandshakeIcon,
  IdCardIcon,
  SettingsIcon,
  SlidersHorizontalIcon,
  UserCheckIcon,
  UserRoundPlusIcon,
  ChevronRightIcon,
} from '../../../icons/animated';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';
import styles from '../NavBar.module.css';

interface SettingsNavLink {
  titleKey: string;
  descriptionKey: string;
  path: string;
  assignedPermissions?: string[];
  assignedSubscription?: string;
  icon: React.ReactNode;
}

const adminSettings = {
  assignedPermissions: [
    Permission.ROLES_VIEW,
    Permission.USERS_VIEW,
    Permission.GROUPS_VIEW,
  ],
  matchAnyPermission: true,
  navLinks: [
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.usersRoles.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.usersRoles.description,
      path: '/access-management',
      assignedPermissions: [Permission.USERS_VIEW],
      icon: (
        <UserRoundPlusIcon size={20} className='text-emerald-600 group-hover:text-emerald-500 transition-colors' />
      ),
    },
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.assessmentFramework.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.assessmentFramework.description,
      path: '/assessment-framework',
      assignedPermissions: [Permission.ASSESSMENTS_VIEW],
      icon: (
        <CompassIcon size={20} className='text-emerald-600 group-hover:text-emerald-500 transition-colors' />
      ),
    },
    {
      titleKey:
        NAVBAR_MENU_KEYS.systemSettings.items.organizationSettings.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.organizationSettings.description,
      path: '/organization-settings',
      assignedPermissions: [Permission.ORGUNIT_VIEW],
      icon: (
        <SlidersHorizontalIcon size={20} className='text-emerald-600 group-hover:text-emerald-500 transition-colors' />
      ),
    },
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.employeeManagement.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.employeeManagement.description,
      path: '/employee-management',
      assignedPermissions: [],
      icon: (
        <IdCardIcon size={20} className='text-emerald-600 group-hover:text-emerald-500 transition-colors' />
      ),
    },
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.candidateManagement.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.candidateManagement.description,
      path: '/candidate-management',
      assignedPermissions: [],
      icon: (
        <UserCheckIcon size={20} className='text-emerald-600 group-hover:text-emerald-500 transition-colors' />
      ),
    },
    {
      titleKey: NAVBAR_MENU_KEYS.systemSettings.items.partnerManagement.title,
      descriptionKey:
        NAVBAR_MENU_KEYS.systemSettings.items.partnerManagement.description,
      path: '/partner-management',
      assignedPermissions: [],
      icon: (
        <HeartHandshakeIcon size={20} className='text-emerald-600 group-hover:text-emerald-500 transition-colors' />
      ),
    },
  ],
};

export const SettingsMenu = () => {
  const { t } = useTranslation(NS.navbarMenus);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (menuOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setMenuOpen(false);
        setIsAnimating(false);
      }, 150);
    } else {
      setMenuOpen(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        if (menuOpen) {
          setIsAnimating(true);
          setTimeout(() => {
            setMenuOpen(false);
            setIsAnimating(false);
          }, 150);
        }
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuOpen) {
        setIsAnimating(true);
        setTimeout(() => {
          setMenuOpen(false);
          setIsAnimating(false);
        }, 150);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [menuOpen]);

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className='relative' ref={menuRef}>
      <button
          onClick={handleMenuToggle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={`${styles.settingsButton} transition-transform duration-200`}
          aria-label="Settings"
      >
        <SettingsIcon isHovered={isHovered} size={20} className='text-[var(--navbar-text,#ffffff)]' />
      </button>

      { menuOpen && (
        <div
            className={`absolute right-0 mt-3 w-72 sm:w-96 max-w-[calc(100vw-16px)] bg-white border border-gray-100 shadow-2xl rounded-xl overflow-hidden backdrop-blur-sm transform transition-all duration-200 ${
              isAnimating
                ? 'opacity-0 scale-95 translate-y-2'
                : 'opacity-100 scale-100 translate-y-0'
            }`}
            style={{ zIndex: 9999 }}
        >
          { /* Header */ }
          <div className='bg-gradient-to-r from-emerald-600 via-emerald-700 to-emerald-800 px-6 py-5 relative overflow-hidden'>
            <div className='absolute inset-0 bg-gradient-to-r from-white/5 to-transparent'></div>
            <div className='relative flex items-center gap-4'>
              <div className='w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm border border-white/20'>
                <SettingsIcon size={20} className='text-white' />
              </div>
              <div>
                <h3 className='text-white font-semibold text-base mb-1'>
                  {t(NAVBAR_MENU_KEYS.systemSettings.title)}
                </h3>
                <p className='text-emerald-100 text-xs opacity-90'>
                  {t(NAVBAR_MENU_KEYS.systemSettings.description)}
                </p>
              </div>
            </div>
          </div>

          { /* Menu Items */ }
          <div className='py-2 max-h-96 overflow-y-auto'>
            { adminSettings.navLinks.map((item) => {
              const isActive = location.pathname === item.path;
              const link = (
                <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => {
                      setIsAnimating(true);
                      setTimeout(() => {
                        setMenuOpen(false);
                        setIsAnimating(false);
                      }, 150);
                    }}
                    className={`group flex items-start gap-4 px-6 py-4 transition-all duration-200 ${
                      isActive
                        ? 'bg-emerald-50 border-r-4 border-emerald-500'
                        : 'hover:bg-gray-50 hover:border-r-4 hover:border-emerald-200'
                    }`}
                >
                  { /* Icon */ }
                  <div
                      className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        isActive
                          ? 'bg-emerald-100 ring-2 ring-emerald-200'
                          : 'bg-gray-100 group-hover:bg-emerald-50'
                      }`}
                  >
                    { item.icon }
                  </div>

                  { /* Content */ }
                  <div className='flex-1 min-w-0'>
                    <div
                        className={`font-medium text-sm transition-colors duration-200 ${
                          isActive
                            ? 'text-emerald-700'
                            : 'text-gray-900 group-hover:text-emerald-600'
                        }`}
                    >
                      { t(item.titleKey) }
                    </div>
                    <div
                        className={`text-xs mt-1 transition-colors duration-200 ${
                          isActive
                            ? 'text-emerald-600'
                            : 'text-gray-500 group-hover:text-emerald-500'
                        }`}
                    >
                      { t(item.descriptionKey) }
                    </div>
                  </div>

                  { /* Active Indicator */ }
                  { isActive && (
                    <div className='flex items-center'>
                      <div className='w-2 h-2 bg-emerald-500 rounded-full animate-pulse'></div>
                    </div>
                  ) }

                  { /* Arrow Icon */ }
                  <div
                      className={`flex-shrink-0 transition-all duration-200 ${
                        isActive
                          ? 'text-emerald-500'
                          : 'text-gray-400 group-hover:text-emerald-400 group-hover:translate-x-1'
                      }`}
                  >
                    <ChevronRightIcon size={16} className='text-gray-400 group-hover:text-emerald-400' />
                  </div>
                </Link>
              );

              const needsGate =
                (item.assignedPermissions?.length ?? 0) > 0 ||
                Boolean((item as any).assignedSubscription);

              return needsGate ? (
                <PermissionGate
                    key={item.path}
                    requiredPermissions={item.assignedPermissions}
                    matchAnyPermission={adminSettings.matchAnyPermission}
                    requiredFeature={(item as any).assignedSubscription}
                >
                  { link }
                </PermissionGate>
              ) : (
                link
              );
            }) }
          </div>

          { /* Footer */ }
          <div className='border-t border-gray-100 px-6 py-3 bg-gray-50'>
            <div className='flex items-center justify-between'>
              <div className='text-xs text-gray-500'>
                {t(NAVBAR_MENU_KEYS.systemSettings.footerConfiguration)}
              </div>
              <div className='flex items-center gap-1 text-xs text-gray-400'>
                <div className='w-2 h-2 bg-green-400 rounded-full'></div>
                <span>{t(NAVBAR_MENU_KEYS.systemSettings.online)}</span>
              </div>
            </div>
          </div>
        </div>
      ) }
    </div>
  );
};
